import { useTranslation } from 'react-i18next';
import CustomModal from '../../../../../components/Modal/CustomModal';
import {
    DescriptionTittle, IconAvatar, IconDepartment,
    QrTitle,
} from '../../../../../components/buttons/IconButtons';
import {useEffect, useRef, useState} from 'react';
import { useDispatch } from 'react-redux';
import { QRCode } from 'react-qrcode-logo';
import { DATE_TIME_FORMAT_NAME_MODEL, getDateFormat} from "../../../../../Apis/functions";
import {GetAccessCodeQR} from "../../../../../reduxToolkit/FoodieAccessCode/FoodieAccessCodeApi";
import {STATUS_MODEL} from "../../../../../enums/statusEnum";
import {downloadQrFile} from "../../ExtraMeal/Model/Models";
import CustomGenericTittleIconDataChip from "../../../../../components/CustomGenericTittleIconDataChip";

/**
 *
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle : Full Stack Developer
 * @Email : sgarcia@ibl.mx
 *
 * AccessCodeSummary component displays a modal with detailed information
 * about a selected access code, including assigned user, requester, start/end times,
 * description, and a QR code if the access code is active.
 *
 * @param {Object} props - Component props
 * @param {Object} props.item - The access code item containing details to display.
 * @param {boolean} props.show - Flag to show or hide the modal.
 * @param {function} props.onHide - Callback function to close the modal.
 * @returns {JSX.Element} - Modal with access code details.
 *
 */
const AccessCodeSummary = ({item, show, onHide}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const qrContainerRef = useRef(null);

    const [data, setData] = useState({
        assignedTo: item?.assignedToName + " " + item?.assignedToLastName + (item?.assignedToSecondLastName ? " " + item?.assignedToSecondLastName : ""),
        requestedBy: item?.requestedByName + " " + item?.requestedByLastName + (item?.requestedBySecondLastName ? item?.requestedBySecondLastName : ""),
        startDate: item?.fromTime,
        endDate: item?.toTime,
        description: item?.description,
        qr: null
    });

    /**
     * Fetches the QR code for the access code if it is active.
     * Updates the `data.qr` state with the QR code string for rendering.
     */
    useEffect(() => {
        if(item?.id && parseInt(item?.statusId) === STATUS_MODEL.ACTIVE) {
            dispatch(GetAccessCodeQR(item?.id)).then(
                ({
                     payload: {
                         data: {data, success},
                     },
                 }) => {
                    if (success) {
                        setData(prev => ({...prev, qr: data ? JSON.stringify(data) : null }));
                    }
                },
            );
        }
    }, [item?.id, dispatch]);

    return (
        <CustomModal
            title={'access_code_summary'}
            show={show}
            buttonConfirm={'download'}
            cancelButton={item?.statusId === STATUS_MODEL.ACTIVE}
            confirmButton={item?.statusId === STATUS_MODEL.ACTIVE}
            onConfirm={() => {
                downloadQrFile(qrContainerRef, t("qr_code"));
                onHide();
            }}
            onHide={onHide}
            classNameBody="pt-2"
        >
            <div className='row mb-4'>
                <div className={'col-sm-12 col-md-6 col-lg-6 col-xl-6'}>
                    <CustomGenericTittleIconDataChip
                        Icon={DescriptionTittle}
                        title={'assigned_to'}
                        name={data?.assignedTo}
                        chipClasses={"w-auto"}
                        IconAvatar={IconAvatar}
                    />
                </div>
                <div className={'col-sm-12 col-md-6 col-lg-6 col-xl-6'}>
                    <CustomGenericTittleIconDataChip
                        Icon={DescriptionTittle}
                        title={'requested_by'}
                        name={data?.requestedBy}
                        chipClasses={"w-auto"}
                        IconAvatar={IconAvatar}
                    />
                </div>
            </div>

            <div className='row mb-4'>
                <div className={'col-sm-12 col-md-6 col-lg-6 col-xl-6'}>
                    <CustomGenericTittleIconDataChip
                        Icon={DescriptionTittle}
                        title={'start_date'}
                        name={getDateFormat(data?.startDate, true, DATE_TIME_FORMAT_NAME_MODEL.shortMonth)}
                        chipClasses={"w-auto"}
                        IconAvatar={IconDepartment}
                        iconColor={"icon-color-shades-400"}
                    />
                </div>
                <div className={'col-sm-12 col-md-6 col-lg-6 col-xl-6'}>
                    <CustomGenericTittleIconDataChip
                        Icon={DescriptionTittle}
                        title={'end_date'}
                        name={getDateFormat(data?.endDate, true, DATE_TIME_FORMAT_NAME_MODEL.shortMonth)}
                        chipClasses={"w-auto"}
                        IconAvatar={IconDepartment}
                        iconColor={"icon-color-shades-400"}
                    />
                </div>
            </div>

            <div className='row mb-4'>
                <div className={'col-12'}>
                    <CustomGenericTittleIconDataChip Icon={DescriptionTittle} title={'description'} />
                    <p className='ibl-font-w-500 mt-2'>{data?.description}</p>
                </div>
            </div>

            {data.qr &&
            <div className='row mb-4'>
                <div className={'col-12'}>
                    <CustomGenericTittleIconDataChip Icon={QrTitle} title={'qr_code'} />
                    <div className='d-flex justify-content-center mt-3'>
                        <div className='vcard-container-modal' ref={qrContainerRef}>
                            <QRCode value={data.qr} size={300} renderAs='canvas' includeMargin={true} />
                        </div>
                    </div>
                </div>
            </div>
            }
        </CustomModal>
    );
};

export default AccessCodeSummary;
